import { useState, useCallback, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { toast } from "@/components/ui/use-toast"
import { Wallet, Copy, Check, LogOut } from "lucide-react"
import { useEthersContext } from "@/providers/EthersProvider"
import { WalletModal } from "@/components/WalletModal"
import { EIP6963ProviderDetail } from "@/hooks/useWalletDiscovery"
import { Interface, formatUnits, Contract, InterfaceAbi } from "ethers"
import { formatUsdBigint } from "@/lib/ethersUtils"
import { createMulticallService } from "@/services/multicallService"
import { getChainProvider } from "@/lib/chainProvider"
import { isPageVisible } from "@/lib/pageVisibility"
import {
  WELEPHANT_ADDRESS,
  STAKING_VAULT_ADDRESS,
  SWAPPER_ADDRESS,
  GAME_CHAIN_ID,
  getMulticall3AddressForChain,
} from "@/lib/contracts"
import { getWelephantAddress } from "@/lib/chains"
import { WelephantSwapABI } from "@/abi/WelephantSwapABI"

// Token logos
import baseLogo from "@/assets/tokens/base.svg"
import bnbLogo from "@/assets/tokens/bnb.svg"
import hardhatLogo from "@/assets/tokens/hardhat.svg"
import welephantLogo from "@/assets/tokens/welephant.svg"
import stWelephantLogo from "@/assets/tokens/stwelephant.svg"
import wethLogo from "@/assets/tokens/weth.svg"

// ERC20 ABI for balance calls
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
] as const

// Token decimals
const WELEPHANT_DECIMALS = 9
const ST_WELEPHANT_DECIMALS = 18

// Chain configurations
export interface ChainTokenConfig {
  name: string
  icon: string
  nativeSymbol: string
  nativeIcon: string
  badgeColor: string // Tailwind bg class for chain dot
  tokens: {
    symbol: string
    icon: string
    decimals: number
    address?: string
    isNative?: boolean
  }[]
}

export const CHAIN_CONFIGS: Record<number, ChainTokenConfig> = {
  56: { // BNB Chain
    name: 'BNB Chain',
    icon: bnbLogo,
    nativeSymbol: 'BNB',
    nativeIcon: bnbLogo,
    badgeColor: 'bg-[rgb(231,187,65)]',
    tokens: [
      { symbol: 'BNB', icon: bnbLogo, decimals: 18, isNative: true },
      { symbol: 'WELEPHANT', icon: welephantLogo, decimals: WELEPHANT_DECIMALS },
      { symbol: 'stWELEPHANT', icon: stWelephantLogo, decimals: ST_WELEPHANT_DECIMALS, address: STAKING_VAULT_ADDRESS },
    ],
  },
  8453: { // Base
    name: 'Base',
    icon: baseLogo,
    nativeSymbol: 'ETH',
    nativeIcon: wethLogo,
    badgeColor: 'bg-[rgb(0,0,245)]',
    tokens: [
      { symbol: 'ETH', icon: wethLogo, decimals: 18, isNative: true },
      { symbol: 'WELEPHANT', icon: welephantLogo, decimals: WELEPHANT_DECIMALS },
    ],
  },
  1337: { // Hardhat
    name: 'Hardhat',
    icon: hardhatLogo,
    nativeSymbol: 'ETH',
    nativeIcon: wethLogo,
    badgeColor: 'bg-[#FFF100]',
    tokens: [
      { symbol: 'ETH', icon: wethLogo, decimals: 18, isNative: true },
      { symbol: 'WELEPHANT', icon: welephantLogo, decimals: WELEPHANT_DECIMALS, address: WELEPHANT_ADDRESS },
      { symbol: 'stWELEPHANT', icon: stWelephantLogo, decimals: ST_WELEPHANT_DECIMALS, address: STAKING_VAULT_ADDRESS },
    ],
  },
  31337: { // Hardhat alternate
    name: 'Hardhat',
    icon: hardhatLogo,
    nativeSymbol: 'ETH',
    nativeIcon: wethLogo,
    badgeColor: 'bg-[#FFF100]',
    tokens: [
      { symbol: 'ETH', icon: wethLogo, decimals: 18, isNative: true },
      { symbol: 'WELEPHANT', icon: welephantLogo, decimals: WELEPHANT_DECIMALS, address: WELEPHANT_ADDRESS },
      { symbol: 'stWELEPHANT', icon: stWelephantLogo, decimals: ST_WELEPHANT_DECIMALS, address: STAKING_VAULT_ADDRESS },
    ],
  },
}

// Default config for unknown chains
const DEFAULT_CHAIN_CONFIG: ChainTokenConfig = {
  name: 'Unknown Network',
  icon: bnbLogo,
  nativeSymbol: 'ETH',
  nativeIcon: wethLogo,
  badgeColor: 'bg-muted-foreground',
  tokens: [
    { symbol: 'ETH', icon: wethLogo, decimals: 18, isNative: true },
  ],
}

// Chains to always fetch balances from (production)
export const PRODUCTION_CHAIN_IDS = [56, 8453]

export interface TokenBalance {
  symbol: string
  icon: string
  balance: bigint
  decimals: number
  usdValue: string | null
  chainId: number
  chainConfig: ChainTokenConfig
}

// Fetch balances for a single chain via its public RPC
export async function fetchChainBalances(
  targetChainId: number,
  config: ChainTokenConfig,
  address: string,
): Promise<TokenBalance[]> {
  const balances: TokenBalance[] = []

  const providerChainId = targetChainId === 1337 || targetChainId === 31337 ? GAME_CHAIN_ID : targetChainId
  const chainProvider = await getChainProvider(providerChainId)
  const multicall3Address = getMulticall3AddressForChain(providerChainId)

  const erc20Iface = new Interface(ERC20_ABI)
  const multicall = createMulticallService(chainProvider, multicall3Address, providerChainId)
  const calls: ReturnType<typeof multicall.encodeCall>[] = []
  const tokenIndexMap: { index: number; token: typeof config.tokens[0] }[] = []

  // Fetch native balance
  const nativeToken = config.tokens.find(t => t.isNative)
  const nativeBalance = await chainProvider.getBalance(address)
  if (nativeToken) {
    balances.push({
      symbol: nativeToken.symbol,
      icon: nativeToken.icon,
      balance: nativeBalance,
      decimals: nativeToken.decimals,
      usdValue: null,
      chainId: targetChainId,
      chainConfig: config,
    })
  }

  // Build ERC20 calls
  for (const token of config.tokens) {
    if (token.isNative) continue
    let tokenAddress = token.address
    if (token.symbol === 'WELEPHANT' && !tokenAddress) {
      tokenAddress = getWelephantAddress(targetChainId)
    }
    if (tokenAddress) {
      tokenIndexMap.push({ index: calls.length, token })
      calls.push(multicall.encodeCall(tokenAddress, erc20Iface, 'balanceOf', [address], true))
    }
  }

  if (calls.length > 0) {
    const results = await multicall.callAndDecode<(bigint | null)[]>(calls)
    for (const { index, token } of tokenIndexMap) {
      balances.push({
        symbol: token.symbol,
        icon: token.icon,
        balance: results[index] ?? 0n,
        decimals: token.decimals,
        usdValue: null,
        chainId: targetChainId,
        chainConfig: config,
      })
    }
  }

  return balances
}

// Fetch USD values for all token balances using the swap contract
export async function fetchUsdValues(balances: TokenBalance[]): Promise<void> {
  if (!SWAPPER_ADDRESS) return

  try {
    const chainProvider = await getChainProvider(GAME_CHAIN_ID)
    const swapContract = new Contract(SWAPPER_ADDRESS, WelephantSwapABI as InterfaceAbi, chainProvider)

    // Process each balance that needs USD value
    for (const token of balances) {
      if (token.balance === 0n) continue

      try {
        if (token.symbol === 'ETH' || token.symbol === 'BNB') {
          // Native tokens - estimate USD directly
          const usdValue = await swapContract.estimateUsdForETH(token.balance)
          token.usdValue = formatUsdBigint(usdValue)
        } else if (token.symbol === 'WELEPHANT') {
          // WELEPHANT - use getWelephantPriceInUSD (handles unwrap calculation internally)
          const pricePerToken: bigint = await swapContract.getWelephantPriceInUSD()
          const usdValue = pricePerToken * token.balance / BigInt(10 ** WELEPHANT_DECIMALS)
          token.usdValue = formatUsdBigint(usdValue)
        } else if (token.symbol === 'stWELEPHANT' && STAKING_VAULT_ADDRESS) {
          // Use vault's actual exchange rate to get underlying WELEPHANT value
          const vaultContract = new Contract(STAKING_VAULT_ADDRESS, [
            "function previewRedeem(uint256 shares) view returns (uint256)",
          ], chainProvider)
          const welephantValue: bigint = await vaultContract.previewRedeem(token.balance)
          if (welephantValue > 0n) {
            const pricePerToken: bigint = await swapContract.getWelephantPriceInUSD()
            const usdValue = pricePerToken * welephantValue / BigInt(10 ** WELEPHANT_DECIMALS)
            token.usdValue = formatUsdBigint(usdValue)
          }
        }
      } catch {
        // Ignore individual token USD errors
      }
    }
  } catch (err) {
    console.error('[WalletConnect] Failed to fetch USD values:', err)
  }
}

export function WalletConnectButton() {
  const { address, isConnected, balance, connect, disconnect, isDevMode, chainId } = useEthersContext()
  const [modalOpen, setModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>([])
  const [isLoadingBalances, setIsLoadingBalances] = useState(false)

  // Connected chain config (for trigger button)
  const connectedChainConfig = chainId ? CHAIN_CONFIGS[chainId] || DEFAULT_CHAIN_CONFIG : DEFAULT_CHAIN_CONFIG

  // Determine which chains to fetch
  const chainsToFetch = useMemo(() => {
    const chains = [...PRODUCTION_CHAIN_IDS]
    // In dev mode, always include the game chain (Hardhat) for local assets
    if (isDevMode && !chains.includes(GAME_CHAIN_ID)) {
      chains.push(GAME_CHAIN_ID)
    }
    return chains
  }, [isDevMode])

  const truncatedAddress = address
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : ""

  const handleSelectWallet = useCallback(async (wallet: EIP6963ProviderDetail) => {
    await connect(wallet.provider)
  }, [connect])

  const handleCopyAddress = useCallback(async () => {
    if (address) {
      try {
        await navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
        toast({
          title: "Address copied",
          description: address,
        })
      } catch (err) {
        console.error("Failed to copy address:", err)
      }
    }
  }, [address])

  // Fetch balances from all chains in parallel
  useEffect(() => {
    if (!address) {
      setTokenBalances([])
      return
    }

    const fetchAllBalances = async () => {
      if (!isPageVisible()) return
      setIsLoadingBalances(true)
      try {
        const results = await Promise.allSettled(
          chainsToFetch.map(cid => {
            const config = CHAIN_CONFIGS[cid]
            if (!config) return Promise.resolve([])
            return fetchChainBalances(cid, config, address)
          })
        )

        const allBalances: TokenBalance[] = []
        for (const result of results) {
          if (result.status === 'fulfilled') {
            allBalances.push(...result.value)
          }
        }
        // Sort: connected chain first, then by chain order in PRODUCTION_CHAIN_IDS
        allBalances.sort((a, b) => {
          const aConnected = a.chainId === chainId ? 0 : 1
          const bConnected = b.chainId === chainId ? 0 : 1
          return aConnected - bConnected
        })
        // Fetch USD values for all tokens using the swap contract
        await fetchUsdValues(allBalances)
        setTokenBalances(allBalances)
      } catch (err) {
        console.error('[WalletConnect] Failed to fetch balances:', err)
      } finally {
        setIsLoadingBalances(false)
      }
    }

    fetchAllBalances()
    const interval = setInterval(fetchAllBalances, 15000)
    return () => clearInterval(interval)
  }, [address, chainsToFetch, chainId])

  if (!isConnected) {
    return (
      <>
        <Button onClick={() => setModalOpen(true)} size="sm">
          <Wallet className="mr-2 h-4 w-4" />
          Connect Wallet
        </Button>
        <WalletModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          onSelectWallet={handleSelectWallet}
        />
      </>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="font-mono"
          >
            <img src={connectedChainConfig.icon} alt={connectedChainConfig.name} className="h-4 w-4 mr-2" />
            <span>{truncatedAddress}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 p-0">
          {/* Network Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <img src={connectedChainConfig.icon} alt={connectedChainConfig.name} className="h-5 w-5" />
            <span className="font-medium">{connectedChainConfig.name}</span>
          </div>

          {/* Address Section */}
          <div className="px-4 py-2 border-b">
            <button
              onClick={handleCopyAddress}
              className="flex items-center gap-2 group hover:bg-muted/50 -mx-2 px-2 py-1.5 rounded-md transition-colors"
            >
              <span className="font-mono text-sm">{truncatedAddress}</span>
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
              )}
            </button>
          </div>

          {/* Token Balances */}
          <div className="px-4 py-2 space-y-2">
            {isLoadingBalances && tokenBalances.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-2">
                Loading balances...
              </div>
            ) : (
              tokenBalances.map((token) => (
                <TokenBalanceRow
                  key={`${token.chainConfig.name}-${token.symbol}`}
                  token={token}
                />
              ))
            )}
          </div>

          {/* Disconnect Button */}
          <div className="px-4 py-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setModalOpen(false); disconnect() }}
              className="w-full justify-end text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Disconnect
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function TokenIconWithChain({
  icon,
  symbol,
  chainName,
  badgeColor,
}: {
  icon: string
  symbol: string
  chainName: string
  badgeColor: string
}) {
  return (
    <div className="relative w-5 h-5" title={chainName}>
      <img src={icon} alt={symbol} className="w-5 h-5" />
      <span
        className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${badgeColor} ring-1 ring-background`}
      />
    </div>
  )
}

function TokenBalanceRow({ token }: { token: TokenBalance }) {
  const formattedBalance = formatTokenBalance(token.balance, token.decimals)

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <TokenIconWithChain
          icon={token.icon}
          symbol={token.symbol}
          chainName={token.chainConfig.name}
          badgeColor={token.chainConfig.badgeColor}
        />
        <span className="text-muted-foreground">{token.symbol}</span>
      </div>
      <div className="text-right">
        <div className="font-medium">{formattedBalance}</div>
        {token.usdValue && (
          <div className="text-[10px] text-muted-foreground">${token.usdValue}</div>
        )}
      </div>
    </div>
  )
}

export function formatTokenBalance(balance: bigint, decimals: number): string {
  const value = parseFloat(formatUnits(balance, decimals))
  if (value === 0) return '0'
  if (value < 0.0001) return '<0.0001'
  if (value < 1) return value.toFixed(4)
  if (value < 1000) return value.toFixed(4)
  if (value < 1000000) return (value / 1000).toFixed(2) + 'K'
  return (value / 1000000).toFixed(2) + 'M'
}
